import React, { useMemo } from 'react';

import { Text } from '@mantine/core';
import cx from 'classnames';

import { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';
import { NoAlertOutlineIcon } from '@/assets/icons/NoAlertOutlineIcon';
import { RedAlertOutlineIcon } from '@/assets/icons/RedAlertOutlineIcon';
import { YellowAlertOutlineIcon } from '@/assets/icons/YellowAlertOutlineIcon';

import { ReportInfo } from '@/types/patientList';

import styles from './cells.module.scss';

interface ReportNameProps {
  report: ReportInfo;
}

export const VendorsToCodeMapping: Record<string, string> = {
  abbott: 'ABT',
  boston_scientific: 'BSC',
  medtronic: 'MDT',
  biotronik: 'BIO',
  sorin: 'SOR'
};

const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[_\s]+/g, ' ') // Replace underscores and multiple spaces with single space
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
    .trim();
};

export const ReportName: React.FC<ReportNameProps> = ({ report }) => {
  const vendorCode = useMemo(() => {
    return VendorsToCodeMapping[report.device?.vendor] ?? 'N/A';
  }, [report.device?.vendor]);

  const reportTypeDisplay = useMemo(() => {
    return toTitleCase(report.reportType || '');
  }, [report.reportType]);

  const getStatusConfig = () => {
    switch (report.color) {
      case 'YELLOW':
        return {
          text: `${vendorCode} Yellow Alert`,
          className: 'yellowAlert',
          icon: <YellowAlertOutlineIcon />
        };
      case 'RED':
        return {
          text: `${vendorCode} Red Alert`,
          className: 'redAlert',
          icon: <RedAlertOutlineIcon />
        };
      default:
        return {
          text: `${vendorCode} ${reportTypeDisplay}`,
          className: 'noAlert',
          icon: <NoAlertOutlineIcon />
        };
    }
  };

  const config = getStatusConfig();

  const handleReportClick = () => {
    if (report.id) {
      const reportDetailUrl = routes.physician.transmissionReportDetail(report.id.toString());
      window.open(reportDetailUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={styles.reportNameContainer}>
      {config.icon}
      <div className={styles.textContainer}>
        <Text
          className={cx(styles.reportName, styles[config.className], styles.clickableReport)}
          onClick={handleReportClick}>
          {config.text} {report.device?.type}
        </Text>
        <Text className={styles.subText} unstyled>
          DOS: {report.billingDate ? formatUtcDate(report.billingDate, 'MM-DD-YYYY') : 'N/A'}
        </Text>
      </div>
    </div>
  );
};
